import { Token } from './types';
import { Logger } from 'loglevel';
import {
  merge,
  MonoTypeOperatorFunction,
  Observable,
  ReplaySubject,
  Subject,
  timer,
  of,
  race
} from 'rxjs';
import { format, UrlObject } from 'url';
import { OpenMessage } from './messages/OpenMessage';
import { EventMessage } from './messages/EventMessage';
import {
  delay,
  filter,
  map,
  mergeMap,
  multicast,
  refCount,
  share,
  take,
  takeUntil,
  tap,
  switchMap,
  first
} from 'rxjs/operators';
import { CommonMessage } from './messages/CommonMessage';
import { MessageType } from './messages/MessageType';
import { WebSocketSubject } from 'rxjs/webSocket';
import { OpenAckMessage } from './messages/OpenAckMessage';
import { JoinAckMessage } from './messages/JoinAckMessage';
import { EventCompleteMessage } from './messages/EventCompleteMessage';
import { LeaveAckMessage } from './messages/LeaveAckMessage';
import { JoinErrorMessage } from './messages/JoinErrorMessage';
import { PingMessage } from './messages/PingMessage';
import { PongMessage } from './messages/PongMessage';
import WebSocket from 'isomorphic-ws';
import { JoinMessage } from './messages/JoinMessage';
import { LeaveMessage } from './messages/LeaveMessage';
import { EventErrorMessage } from './messages/EventErrorMessage';
import { generateTraceId } from './utils/generateTraceId';
import { LeaveErrorMessage } from './messages/LeaveErrorMessage';

export type GetToken = () => Promise<Token>;

export interface AdonisConfig {
  url: UrlObject;
  heartbeatInterval: number;
}

export interface Map {
  [key: string]: any;
}

export interface TopicConfig {
  name: string;
  parameters?: Map;
  metadata?: Map;
  isRaw?: boolean;
  isAuthorized?: boolean;
}

type TopicMessage<T> =
  | JoinAckMessage
  | JoinMessage
  | EventMessage<T>
  | EventErrorMessage
  | LeaveMessage
  | EventCompleteMessage
  | LeaveAckMessage
  | LeaveErrorMessage;

export type AdonisMessage<T> =
  | TopicMessage<T>
  | PongMessage
  | PingMessage
  | OpenMessage
  | OpenAckMessage;

export enum TopicStatus {
  OPEN,
  CLOSED
}

export interface StatusObservable<T> extends Observable<T> {
  status: Subject<any>;
}

export class AdonisClient {
  private session: Observable<OpenAckMessage>;
  private ws!: WebSocketSubject<CommonMessage>;
  private messages: Observable<CommonMessage>;
  private openObserver = new Subject<Event>();
  private closeObserver = new Subject();
  private logoutObserver = new Subject();
  private lastSubscriptionId: number = 1;
  private getToken?: GetToken;
  private logger: Logger;
  private isSocketConnected: boolean = false;

  constructor(config: AdonisConfig, logger: Logger, getToken?: GetToken) {
    this.getToken = getToken;
    this.logger = logger;

    this.messages = new Observable<CommonMessage>(observer => {
      this.ws = new WebSocketSubject<CommonMessage>({
        url: format(config.url),
        WebSocketCtor: WebSocket as any,
        openObserver: this.openObserver,
        closeObserver: this.closeObserver,
        deserializer: e => {
          const message = JSON.parse(e.data);

          switch (message.t) {
            case MessageType.OPEN_ACK:
              return new OpenAckMessage(message.d);
            case MessageType.JOIN_ACK:
              return new JoinAckMessage(message.d);
            case MessageType.JOIN_ERROR:
              return new JoinErrorMessage(message.d);
            case MessageType.EVENT:
              return new EventMessage(message.d);
            case MessageType.EVENT_COMPLETE:
              return new EventCompleteMessage(message.d);
            case MessageType.LEAVE_ACK:
              return new LeaveAckMessage(message.d);
            case MessageType.LEAVE_ERROR:
              return new LeaveErrorMessage(message.d);
            case MessageType.PONG:
              return new PongMessage();
            case MessageType.EVENT_ERROR:
              return new EventErrorMessage(message.d);
            default:
              throw new Error('Unknown package ' + e);
          }
        }
      });

      const subs = this.ws.subscribe(
        data => {
          observer.next(data);
        },
        err => {
          observer.error(err);
        },
        () => {
          observer.complete();
        }
      );

      return () => {
        subs.unsubscribe();
      };
    }).pipe(share());

    this.closeObserver.subscribe(event => {
      this.isSocketConnected = false;
    });

    this.session = this.openObserver.pipe(
      tap(() => {
        this.ws.next(new OpenMessage({}));
        this.isSocketConnected = true;
      }),
      mergeMap(() => {
        return this.ws.pipe(filter(message => message instanceof OpenAckMessage));
      }),
      tap(() => {
        this.logger.debug('The greeting is finished');
      }),
      this.heartbeat(config.heartbeatInterval),
      multicast<any>(() => new ReplaySubject(1)),
      refCount()
    );
  }

  topic<T>(config: TopicConfig): StatusObservable<T> {
    const metadata = {
      traceId: generateTraceId(),
      spanId: generateTraceId(),
      ...config.metadata
    };

    const status = new Subject();
    const dataStream = this.session.pipe(source => {
      const resultObservable = new Observable<AdonisMessage<T>>(observer => {
        const subscriptionId = this.lastSubscriptionId++;

        const topicMessages = this.messages.pipe(
          filter((message: TopicMessage<T>) => {
            return message.d.i === subscriptionId;
          })
        );

        topicMessages
          .pipe(
            filter(message => message.t === MessageType.JOIN_ACK),
            tap(() => status.next({ status: TopicStatus.OPEN }))
          )
          .subscribe(observer);

        const completeMessage = topicMessages.pipe(
          filter(
            message =>
              message.t === MessageType.LEAVE_ACK || message.t === MessageType.EVENT_COMPLETE
          ),
          take(1),
          tap(() => status.next({ status: TopicStatus.CLOSED }))
        );

        let token = tap();
        let logout: MonoTypeOperatorFunction<AdonisMessage<T>> = tap();

        if (config.isAuthorized && this.getToken) {
          token = mergeMap(() =>
            this.getToken!().catch(err => {
              throw `Can't get token. Message: ${err}`;
            })
          );
          logout = takeUntil(this.logoutObserver);
        }

        const sub = source
          .pipe(
            tap(msg => {
              this.logger.debug(
                `Trying to create new subscription: ${config.name} id=${subscriptionId}`
              );
            }),
            token,
            tap(msg => {
              this.ws.next(
                new JoinMessage({
                  topic: config.name,
                  i: subscriptionId,
                  accessToken: (msg as Token).accessToken,
                  metadata,
                  parameters: config.parameters
                })
              );
            }),
            mergeMap(() => topicMessages),
            map((message: TopicMessage<T>) => {
              if (message instanceof JoinErrorMessage) {
                this.logger.debug("Can't join to topic " + config.name + '. Message: ' + message.d.error);
                status.next({ status: TopicStatus.CLOSED, error: message.d.error });
                throw message.d.error;
              }

              if (message instanceof EventErrorMessage) {
                this.logger.debug("Subscription " + config.name + ' ended. Message: ' + message.d.error);
                status.next({ status: TopicStatus.CLOSED, error: message.d.error });
                throw message.d.error;
              }

              return message;
            }),
            takeUntil(completeMessage),
            logout
          )
          .subscribe(observer);

        return () => {
          if (this.isSocketConnected) {
            this.ws.next(new LeaveMessage({ i: subscriptionId }));
          }
          status.next({ status: TopicStatus.CLOSED });
          sub.unsubscribe();
          this.logger.debug('Close subscription id=' + subscriptionId);
        };
      });

      return resultObservable;
    });

    if (config.isRaw) {
      // @ts-ignore
      dataStream.status = status;
      return (dataStream as any) as StatusObservable<T>;
    }

    const result = (dataStream.pipe(
      filter((message): message is EventMessage<T> => message instanceof EventMessage),
      map(message => {
        return message.d.d;
      })
    ) as any) as StatusObservable<T>;

    // @ts-ignore
    result.status = status;
    return result;
  }

  heartbeat(timeout: number) {
    const PONG_TIMEOUT = timeout - 200;

    const ping = timer(1000, timeout).pipe(
      tap(() => {
        this.ws.next(new PingMessage());
      }),
      switchMap(() => {
        return race(pong, throwAfterDelay);
      }),
      filter(() => false)
    );

    const pong = this.messages.pipe(
      filter(message => {
        return message.t === MessageType.PONG;
      })
    );

    const throwAfterDelay = of(0).pipe(
      delay(PONG_TIMEOUT),
      map(() => {
        this.logger.debug('Can`t see pong message at least ' + PONG_TIMEOUT + 'ms');
        throw new Error('Can`t see pong message at least ' + PONG_TIMEOUT + 'ms');
      })
    );

    if (timeout === 0) {
      return <T>(source: Observable<T>) => source;
    } else {
      return <T>(source: Observable<T>) => merge(source, ping);
    }
  }

  terminate() {
    if (this.ws) {
      this.ws.unsubscribe();
      return this.closeObserver.pipe(first()).toPromise();
    }
    return Promise.resolve();
  }

  logout() {
    this.logoutObserver.next();
  }
}
